import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store';
import { api, getApiErrorMessage } from '../lib/api';
import Modal from './Modal';
import { Project, ImpactDomain } from '../types';

// The three dismissible tip cards the user can flip through with Next.
// Order matches the i18n onboarding.tips.{invite,groups,explore} blocks
// in src/i18n/locales/*.json — do not reorder without also reordering
// the JSON keys, since we look titles/bodies up by index.
const TIP_KEYS = ['invite', 'groups', 'explore'] as const;

// First-run onboarding wizard. Skeleton only in this packet:
//   - fetches /projects + /workspace/domains in parallel,
//   - decides which step (if any) the user needs via live gating,
//   - renders a Modal with placeholder text and a disabled submit button.
//
// Real form/submit logic for steps 1 and 2 is intentionally deferred to
// later packets — we only ship the gating skeleton here so the wizard
// shows/hides correctly based on the user's existing data.
const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const [projects, setProjects] = useState<Project[]>([]);
  const [domains, setDomains] = useState<ImpactDomain[]>([]);
  const [loading, setLoading] = useState(true);
  // workspaces.owner_id is set once, at registration, to the id of the
  // user who created the workspace (auth.service.ts's register flow) and
  // is never assigned to anyone else — invited members never become the
  // owner. This wizard is a first-run setup flow for whoever set up the
  // workspace, not something every invited teammate should be forced
  // through, so it's gated on ownership below.
  const [workspaceOwnerId, setWorkspaceOwnerId] = useState<string | null>(null);

  // Optimistic "wizard hidden for this session" flag. The backend write
  // happens in dismissTips() below — if it succeeds, useAuthStore.user
  // also gets onboarding_tips_dismissed=true, which causes the gating
  // logic to return null on the very next render even without this flag.
  // We keep the flag as a belt-and-suspenders optimistic hide in case
  // the PUT fails and we still want the modal to disappear this session.
  const [tipsDismissed, setTipsDismissed] = useState(false);

  // Which tip card is showing (0..2). Advances with Next, resets only on
  // a fresh mount of the wizard.
  const [tipIndex, setTipIndex] = useState(0);

  // Step 1 (project) form state. Reset on success — on the next render
  // the gating logic will see the freshly-added project in `projects`
  // and advance the wizard to the domain step.
  const [projectName, setProjectName] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);

  // Step 2 (domain) form state. Same lifecycle as step 1: on success the
  // freshly-added domain flips needsDomain to false, and the next render
  // either advances to the tips step or hides the wizard entirely.
  // default_group_id is intentionally omitted from this form (Settings
  // page can set it later); onboarding.step2.groupLabel/groupNone are
  // therefore unused in this file on purpose.
  const [domainName, setDomainName] = useState('');
  const [domainDescription, setDomainDescription] = useState('');
  const [domainSubmitting, setDomainSubmitting] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  // POST /projects — mirrors Settings.tsx's handleAddProject but scoped
  // down to the onboarding form's three fields (no member/group seeding
  // in this wizard; that lives in the full Settings page).
  const handleCreateProject = async () => {
    if (submitting || !projectName || !projectKey) return;
    setSubmitting(true);
    setProjectError(null);
    try {
      const payload: Record<string, any> = {
        name: projectName,
        project_key: projectKey
      };
      if (projectDescription) payload.description = projectDescription;
      const res = await api.post('/projects', payload);
      const created = (res.data as any)?.data as Project | undefined;
      if (created) {
        setProjects((prev) => [...prev, created]);
      }
      // Form is cleared regardless — success moves gating forward,
      // and on failure we still want stale input gone so the user
      // doesn't accidentally resubmit the same broken values.
      setProjectName('');
      setProjectKey('');
      setProjectDescription('');
    } catch (err) {
      setProjectError(getApiErrorMessage(err, 'Failed to create project.'));
    } finally {
      setSubmitting(false);
    }
  };

  // POST /workspace/domains — mirrors Settings.tsx's handleAddDomain but
  // scoped down to name + description (no group picker in this wizard;
  // default_group_id stays optional in the backend and can be set later
  // from the Settings page).
  const handleCreateDomain = async () => {
    if (domainSubmitting || !domainName) return;
    setDomainSubmitting(true);
    setDomainError(null);
    try {
      const res = await api.post('/workspace/domains', {
        name: domainName,
        description: domainDescription || null
      });
      const created = (res.data as any)?.data as ImpactDomain | undefined;
      if (created) {
        setDomains((prev) => [...prev, created]);
      }
      // Clear on both success and failure, same reasoning as step 1.
      setDomainName('');
      setDomainDescription('');
    } catch (err) {
      setDomainError(getApiErrorMessage(err, 'Failed to create domain.'));
    } finally {
      setDomainSubmitting(false);
    }
  };

  // Persist onboarding_tips_dismissed=true to /auth/me and sync the
  // in-memory user. Mirrors Settings.tsx's saveMyProfile useAuthStore
  // patch, with an extra explicit `onboarding_tips_dismissed: true` so
  // we override whatever the server returns (the user has just confirmed
  // they want the tips gone — that intent should win over a stale value).
  const dismissTips = async () => {
    setTipsDismissed(true); // optimistic — hide immediately
    try {
      const res = await api.put('/auth/me', { onboarding_tips_dismissed: true });
      const updated = (res.data as any)?.data;
      useAuthStore.setState((prev) => ({
        user: prev.user
          ? { ...prev.user, onboarding_tips_dismissed: true, ...(updated || {}) }
          : prev.user
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('OnboardingWizard: failed to persist onboarding_tips_dismissed', err);
      // Wizard stays hidden for this session (tipsDismissed is already true);
      // it may reappear on next login if the write never landed — acceptable
      // fallback, not worth blocking the user over.
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [projectsRes, domainsRes, workspaceRes] = await Promise.all([
          api.get('/projects'),
          api.get('/workspace/domains'),
          api.get('/workspace/me')
        ]);
        if (cancelled) return;
        const projectsData = (projectsRes.data as any)?.data;
        const domainsData = (domainsRes.data as any)?.data;
        const workspaceData = (workspaceRes.data as any)?.data;
        if (Array.isArray(projectsData)) setProjects(projectsData);
        if (Array.isArray(domainsData)) setDomains(domainsData);
        if (workspaceData) setWorkspaceOwnerId(workspaceData.owner_id ?? null);
      } catch (err) {
        // On error, workspaceOwnerId stays null, which makes the isOwner
        // check below false — the wizard simply stays hidden rather than
        // risking showing a mandatory-setup flow to someone who isn't
        // actually the workspace owner.
        // eslint-disable-next-line no-console
        console.error('OnboardingWizard: failed to load gating data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // While the gating data is still loading, render nothing — prevents
  // a flash of the wizard for users who already have projects+domains
  // (the common case, so we don't want any perceptible flicker).
  if (loading) return null;

  // This is a first-run setup flow for whoever created the workspace —
  // invited teammates (operators, managers, client users added later)
  // must never be forced through it, even if the workspace still only has
  // the default project/zero domains because the owner hasn't finished
  // setting up yet.
  const isOwner = !!user && !!workspaceOwnerId && user.id === workspaceOwnerId;
  if (!isOwner) return null;

  // Gating (live-derived, no persisted state needed for steps 1 & 2).
  // There's no separate system-created "Genel" project anymore (removed
  // 2026-09-05) — a fresh workspace has zero projects, and whichever
  // project the owner creates here becomes the workspace's default. So
  // "needs a project" is just "has none yet", no is_default filtering.
  const needsProject = projects.length === 0;
  const needsDomain = domains.length === 0;
  const needsTips =
    !needsProject &&
    !needsDomain &&
    !user?.onboarding_tips_dismissed &&
    !tipsDismissed;

  // Priority order: project → domain → tips.
  const step: 'project' | 'domain' | 'tips' | null = needsProject
    ? 'project'
    : needsDomain
    ? 'domain'
    : needsTips
    ? 'tips'
    : null;

  if (step === null) return null;

  // Current step number shown in the progress hint (1/2/3).
  const current = step === 'project' ? 1 : step === 'domain' ? 2 : 3;
  const total = 3;

  return (
    <Modal>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Progress hint — always at the very top, same on every step */}
        <div className="px-6 pt-4 pb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
          {t('onboarding.progress', { current, total })}
        </div>

        {step === 'tips' ? (
          // Tips step is dismissible: show the close (X) button + a
          // Next/Skip state machine that walks through the three tip
          // cards (invite → groups → explore) and, on Skip or Finish,
          // persists onboarding_tips_dismissed=true via dismissTips().
          <>
            <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t(`onboarding.tips.${TIP_KEYS[tipIndex]}.title`)}
              </h2>
              <button
                type="button"
                onClick={dismissTips}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(`onboarding.tips.${TIP_KEYS[tipIndex]}.body`)}
              </p>
              <div className="flex justify-between items-center pt-2">
                {/* Skip is always visible and goes straight to dismissal
                    (same backend write as Finish). */}
                <button
                  type="button"
                  onClick={dismissTips}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium py-2 px-4 transition-colors"
                >
                  {t('onboarding.tips.skip')}
                </button>
                {tipIndex < TIP_KEYS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setTipIndex(tipIndex + 1)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    {t('onboarding.tips.next')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={dismissTips}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    {t('onboarding.tips.finish')}
                  </button>
                )}
              </div>
            </div>
          </>
        ) : step === 'project' ? (
          // Step 1 — real form (name / project_key / optional description).
          // On success we append the new project to `projects`; the existing
          // gating logic then flips needsProject to false and the next render
          // moves on to the domain step without a page refresh.
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('onboarding.step1.title')}
              </h2>
              <span className="w-6 h-6" aria-hidden="true" />
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="onboarding-project-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.step1.nameLabel')}
                </label>
                <input
                  id="onboarding-project-name"
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder={t('onboarding.step1.namePlaceholder') as string}
                  disabled={submitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="onboarding-project-key" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.step1.keyLabel')}
                </label>
                <input
                  id="onboarding-project-key"
                  type="text"
                  value={projectKey}
                  onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
                  placeholder={t('onboarding.step1.keyPlaceholder') as string}
                  disabled={submitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('onboarding.step1.keyHint')}
                </p>
              </div>

              <div>
                <label htmlFor="onboarding-project-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.step1.descriptionLabel')}
                </label>
                <textarea
                  id="onboarding-project-description"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {projectError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {projectError}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleCreateProject}
                  disabled={submitting || !projectName || !projectKey}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? '...' : t('onboarding.step1.submit')}
                </button>
              </div>
            </div>
          </>
        ) : (
          // Step 2 (domain) — real form (name + optional description).
          // On success the new domain is appended to `domains`; gating
          // then flips needsDomain to false and the next render advances
          // to the tips step (or hides the wizard entirely when
          // onboarding_tips_dismissed is already set).
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('onboarding.step2.title')}
              </h2>
              <span className="w-6 h-6" aria-hidden="true" />
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="onboarding-domain-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.step2.nameLabel')}
                </label>
                <input
                  id="onboarding-domain-name"
                  type="text"
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  placeholder={t('onboarding.step2.namePlaceholder') as string}
                  disabled={domainSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="onboarding-domain-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.step2.descriptionLabel')}
                </label>
                <textarea
                  id="onboarding-domain-description"
                  value={domainDescription}
                  onChange={(e) => setDomainDescription(e.target.value)}
                  disabled={domainSubmitting}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {domainError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {domainError}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleCreateDomain}
                  disabled={domainSubmitting || !domainName}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {domainSubmitting ? '...' : t('onboarding.step2.submit')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default OnboardingWizard;
